import pdfMake from "../boot/pdfmake.js";
import { cloneDeep, defaultsDeep, isArray, isString, omit } from "lodash";
import { formatCurrency } from "../util/misc.js";
import { getLocationCompany, getLocationInfo } from "../constants.js";

import Address from "../classes/Address.js";
import Phone from "../classes/Phone.js";
import { i18n } from "../boot/i18n.js";

// Upper bound for a single image fetch in fetchImage. Every drawReport awaits
// its image fetches with Promise.all, so one request that never settles would
// hang PDF generation — and the spinner of whichever flow triggered it —
// indefinitely. Generous enough for a large photo on a phone's connection.
const IMAGE_FETCH_TIMEOUT = 30000;

const styles = {
  default: {
    bold: false,
    fontSize: 10.6,
    lineHeight: 1.25,
  },
  bold: {
    bold: true,
  },
  italic: {
    italics: true,
  },
  underline: {
    decoration: "underline",
  },
  strikethrough: {
    decoration: "lineThrough",
  },
  link: {
    decoration: "underline",
    color: "blue",
  },
  medium: {
    fontSize: 10.6,
  },
  small: {
    fontSize: 9,
  },
  right: {
    alignment: "right",
  },
  negative: {
    color: "#c10015",
  },
  h4: {
    fontSize: 25.5,
    lineHeight: 1.18,
  },
  h6: {
    fontSize: 15,
    lineHeight: 1.18,
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 1.5,
  },
  caption: {
    fontSize: 9.6,
    lineHeight: 1.3,
  },
  terms: {
    fontSize: 9,
    lineHeight: 1.2,
    alignment: "justify",
  },
  id: {
    font: "Roboto Mono",
    bold: true,
  },
  code: {
    font: "Roboto Mono",
    fontSize: 8,
    lineHeight: 1.5,
  },
  lineItemNumber: {
    fontSize: 13,
    bold: true,
    alignment: "center",
    margin: [0, 5],
  },
  itemDescription: {
    fontSize: 8,
    lineHeight: 1.2,
    alignment: "justify",
  },
  issueDescription: {
    fontSize: 9,
    lineHeight: 1.2,
    alignment: "justify",
  },
};

export default class PDF {
  constructor() {
    this.doc = {};
    defaultsDeep(
      this.doc,
      {
        pageSize: "LETTER",
        pageMargins: [18, 18, 18, 18],
        defaultStyle: styles.default,
        styles,
        content: [],
      },
      ...arguments
    );
    const width = 612;
    const height = 792;
    this.page = {
      width,
      height,
      fold: height / 3,
      l: this.doc.pageMargins[0],
      t: this.doc.pageMargins[1],
      r: width - this.doc.pageMargins[2],
      b: height - this.doc.pageMargins[3],
      innerWidth: width - this.doc.pageMargins[0] - this.doc.pageMargins[2],
      innerHeight: height - this.doc.pageMargins[1] - this.doc.pageMargins[3],
    };
    this.borderColor = "#ccc";
    this.hrDefaults = {
      width: this.page.innerWidth,
      lineWidth: 1,
      lineColor: this.borderColor,
      absolutePosition: undefined,
    };
  }

  draw() {
    this.doc.content.push(...arguments);
  }

  create() {
    return pdfMake.createPdf(this.doc);
  }

  measure(content) {
    content = cloneDeep(content);
    delete content.absolutePosition;
    delete content.relativePosition;
    let size;
    const pdf = pdfMake.createPdf({
      ...this.doc,
      content: [
        {
          table: {
            headerRows: 0,
            body: [[content]],
          },
          layout: {
            defaultBorder: false,
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingTop: (rowIndex, node) => {
              size = measureTableRow(node, rowIndex);
              return 0;
            },
            paddingBottom: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 0,
          },
        },
      ],
    });
    pdf.getStream({});
    size.content = content;
    return size;
  }

  // Number of pages `content` occupies when laid out alone, starting at the
  // top of an empty page. Used to detect content that cannot fit on a single
  // page, which pdfmake drops silently when it is inside an unbreakable block.
  pageCount(content) {
    content = cloneDeep(content);
    delete content.absolutePosition;
    delete content.relativePosition;
    delete content.pageBreak;
    let pages = 1;
    const pdf = pdfMake.createPdf({
      ...this.doc,
      content: [content],
      header: undefined,
      footer: (currentPage, totalPages) => {
        pages = totalPages;
        return null;
      },
    });
    pdf.getStream({});
    return pages;
  }

  // pdfmake puts a table's header rows and the `keepWithHeaderRows` rows that
  // follow them into a single unbreakable block, and `dontBreakRows` makes
  // every row unbreakable too. A block taller than a page can never be
  // committed, so pdfmake discards it without raising anything: the row
  // vanishes from the document and the page it should have filled comes out
  // blank. Relax whichever of the two options would hide a row, leaving them
  // in place for every table whose rows do fit.
  fitOversizedRows(table) {
    const spec = table.table;
    const headerRows = spec.headerRows || 0;
    const keptRows = headerRows + (spec.keepWithHeaderRows || 0);
    const needsSplitting = (body) =>
      this.pageCount({
        ...table,
        table: {
          ...spec,
          body,
          // pdfmake rejects a table with fewer rows than `headerRows`, and a
          // probe body can be shorter than the header of the table it is cut
          // from — a single row measured against a two row category header.
          headerRows: Math.min(headerRows, body.length),
          dontBreakRows: false,
          keepWithHeaderRows: 0,
        },
      }) > 1;

    if (keptRows > headerRows && needsSplitting(spec.body.slice(0, keptRows))) {
      spec.keepWithHeaderRows = 0;
    }
    if (spec.dontBreakRows && spec.body.some((row) => needsSplitting([row]))) {
      spec.dontBreakRows = false;
    }
    return table;
  }

  phone(number) {
    if (number.data) {
      number = number.data;
    }
    if (isString(number)) {
      number = { number };
    } else {
      number = omit(number, "country");
    }
    return new Phone(number).toString();
  }

  address(address) {
    let output = [];
    if (address.title) {
      output.push({
        text:
          address.title.toUpperCase() + (address.hasChanges ? "*" : "") + ":",
        style: ["bold", address.hasChanges ? "negative" : ""],
      });
    }
    if (!(address instanceof Address)) {
      address = new Address(address);
    }
    output.push(address.toString().split("\n"));
    return output;
  }

  currency(value) {
    return formatCurrency(value);
  }

  async header({ meta, location, contact, contactTitle, addresses }) {
    if (isString(location)) {
      location = getLocationInfo(location);
    }
    const company = getLocationCompany(location);
    const logoInfo = location.company.logo;

    // Fetch SVG logo as text (Vite-compatible; webpack raw-loader no longer used)
    const logoUrl = `/logos/${logoInfo.src}`;
    const logoResponse = await fetch(logoUrl);
    const logoSvg = await logoResponse.text();

    const logo = {
      svg: logoSvg,
      height: logoInfo.position.height,
      relativePosition: logoInfo.position,
    };

    const companyAddress = {
      stack: this.address(location.address),
      style: "caption",
      relativePosition: { ...logoInfo.addressPosition },
    };

    const companyContactInfo = { stack: [], style: "caption" };
    if (company.phone) {
      let phone = this.phone(company.phone);
      if (company.fax) {
        phone = i18n.global.t("Phone") + ": " + phone;
      }
      companyContactInfo.stack.push(phone);
    }
    if (company.fax) {
      companyContactInfo.stack.push(
        i18n.global.t("Fax") + ": " + this.phone(company.fax)
      );
    }
    if (company.email) {
      companyContactInfo.stack.push(company.email.address);
    }

    const companyAddressSize = this.measure(companyAddress);
    const bottom = logoInfo.addressPosition.y + companyAddressSize.height;
    const companyContactInfoSize = this.measure(companyContactInfo);
    let x = logoInfo.addressPosition.x + companyAddressSize.width + 12;
    let y =
      bottom -
      Math.max(companyAddressSize.height, companyContactInfoSize.height);
    companyContactInfo.relativePosition = { x, y };

    if (companyAddressSize.height < companyContactInfoSize.height) {
      companyAddress.relativePosition.y -=
        companyContactInfoSize.height - companyAddressSize.height;
    }

    this.draw(logo, companyAddress, companyContactInfo);

    // User Contact Info
    if (contact) {
      const contactInfo = {
        stack: [
          {
            text:
              (
                contactTitle || i18n.global.t("Contact Information")
              ).toUpperCase() + ":",
            style: "bold",
          },
          contact.name,
        ],
      };
      if (contact.phones.length) {
        contactInfo.stack.push({
          text: this.phone(contact.phones[0]),
          style: "caption",
        });
      }
      contactInfo.stack.push({
        text: contact.emails[0].address,
        style: "caption",
      });

      const contactInfoSize = this.measure(contactInfo);
      x += companyContactInfoSize.width + 18;
      y = bottom - contactInfoSize.height;
      contactInfo.relativePosition = { x, y };
      this.draw(contactInfo);
    } else {
      x += 150;
    }

    // Meta Info
    if (meta) {
      const stack = [];
      if (meta.title) {
        stack.push({
          text: meta.title,
          style: "h4",
        });
      }
      if (meta.subtitle) {
        stack.push({
          [isArray(meta.subtitle) ? "stack" : "text"]: meta.subtitle,
          style: "subtitle",
        });
      }
      if (meta.qr) {
        stack.push({
          qr: meta.qr,
          fit: 74,
        });
      }
      if (meta.id) {
        stack.push({
          [isArray(meta.id) ? "stack" : "text"]: meta.id,
          style: "code",
        });
      }
      this.draw({ stack, style: { alignment: "right" } });
    }

    // Addresses
    if (addresses && addresses.length) {
      x += this.page.l;
      y = 163;
      if (addresses.length > 1) {
        this.draw({
          stack: this.address(addresses[1]),
          absolutePosition: { x, y },
        });
      }
      x = 91;
      this.draw({
        stack: this.address(addresses[0]),
        absolutePosition: { x, y },
      });

      this.draw(
        this.hr({
          lineWidth: 2,
          absolutePosition: {
            x: this.page.l,
            y: this.page.fold,
          },
        })
      );
    }
  }

  hr(options = {}) {
    defaultsDeep(options, this.hrDefaults);
    return {
      canvas: [
        {
          type: "line",
          x1: 0,
          y1: 0,
          x2: options.width,
          y2: 0,
          lineWidth: options.lineWidth,
          lineColor: options.lineColor,
        },
      ],
      absolutePosition: options.absolutePosition,
    };
  }

  signature(pageBottom = false, svg = null) {
    // Signature
    const dateWidth = 150;
    const margin = 15;
    const textHeight = 14;

    if (svg) {
      if (svg.startsWith("data:image/svg+xml;base64,")) {
        svg = window.atob(svg.slice(26));
      }
      const svgHeight = Number(svg.match(/height="(\d+\.?\d*)"/)[1]);
      const svgWidth = Number(svg.match(/width="(\d+\.?\d*)"/)[1]);
      const scale = 0.65;
      const height = svgHeight * scale;
      const width = svgWidth * scale;
      this.draw({
        svg,
        width,
        height,
        absolutePosition: pageBottom
          ? {
              x: this.page.l + (this.page.innerWidth - width) / 2,
              y: this.page.b - height + 10,
            }
          : undefined,
      });
    } else {
      this.draw({
        margin: [margin, 0, margin, 0],
        columns: [
          {
            stack: [
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: this.page.innerWidth - dateWidth - margin * 4,
                    y2: 0,
                    lineWidth: 1,
                    lineColor: "#000",
                  },
                ],
              },
              {
                text: i18n.global.t("Signature"),
                style: "small",
              },
            ],
          },
          {
            stack: [
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: dateWidth,
                    y2: 0,
                    lineWidth: 1,
                    lineColor: "#000",
                  },
                ],
              },
              {
                text: i18n.global.t("Date"),
                style: "small",
              },
            ],
          },
        ],
        columnGap: margin * 2,
        absolutePosition: pageBottom
          ? {
              x: this.page.l + margin,
              y: this.page.b - textHeight,
            }
          : undefined,
      });
    }
  }

  fromHTML(html) {
    function printElement(el, style = []) {
      let output = el.innerText;
      if (el.children.length) {
        output = [];
        Array.from(el.children).forEach((child) => {
          let childStyle = style.concat();
          if (
            !output.length &&
            child.previousSibling &&
            child.previousSibling.nodeType === 3
          ) {
            output.push({ text: child.previousSibling.textContent, style });
          }
          switch (child.tagName) {
            case "B":
            case "STRONG":
              childStyle.push("bold");
              break;
            case "I":
            case "EM":
              childStyle.push("italic");
              break;
            case "U":
              childStyle.push("underline");
              break;
            case "STRIKE":
              childStyle.push("strikethrough");
              break;
          }
          const text = printElement(child, childStyle);
          output.push({ text, style: childStyle });
          if (child.nextSibling && child.nextSibling.nodeType === 3) {
            output.push({ text: child.nextSibling.textContent, style });
          }
        });
      }
      return output;
    }
    const root = document.createElement("DIV");
    root.innerHTML = html;
    return printElement(root);
  }

  async fetchImage(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), IMAGE_FETCH_TIMEOUT);
    let blob;
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        // Without this a 404/403 from Storage yields an error document that
        // gets embedded as a "data URL", failing later inside pdfmake with a
        // message that says nothing about which image was at fault.
        console.error(`Failed to fetch image (${response.status}): ${url}`);
        throw new Error("pdfImageUnavailable");
      }
      blob = await response.blob();
    } catch (error) {
      if (controller.signal.aborted) {
        console.error(`Timed out fetching image: ${url}`);
        throw new Error("pdfImageUnavailable");
      }
      throw error;
    } finally {
      clearTimeout(timer);
    }
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // Detect SVG from fetched dataURL, not just item.image.ext, since some
  // legacy/malformed item.image entries may be missing ext while the
  // underlying file is actually SVG (content-type: image/svg+xml).
  isSvgImage(item, imageData) {
    if (item && item.image && item.image.ext === "svg") return true;
    return (
      typeof imageData === "string" &&
      imageData.startsWith("data:image/svg+xml")
    );
  }
}

function findInlineSize(cell, maxWidth, usedWidth = 0) {
  const calcLines = (inlines, maxWidth, usedWidth) => {
    if (inlines === undefined) {
      return { height: 0, width: 0 };
    }
    let currentMaxHeight = 0;
    for (const currentNode of inlines) {
      usedWidth += currentNode.width;
      if (usedWidth > maxWidth) {
        currentMaxHeight += currentNode.height;
        usedWidth = currentNode.width;
      } else {
        currentMaxHeight = Math.max(currentNode.height, currentMaxHeight);
      }
    }
    return { height: currentMaxHeight, width: usedWidth };
  };

  const calcStack = (stack) => {
    const size = stack
      .map((item) => {
        if (item.stack) {
          return calcStack(item.stack);
        } else {
          return calcLines(item._inlines, item._maxWidth, 0);
        }
      })
      .reduce((prev, next) => {
        return {
          height: prev.height + next.height,
          width: next.width,
        };
      });
    return size;
  };

  if (cell._offsets) {
    usedWidth += cell._offsets.total;
  }

  if (cell._inlines && cell._inlines.length) {
    return calcLines(cell._inlines, maxWidth, usedWidth);
  } else if (cell.stack && cell.stack[0]) {
    return calcStack(cell.stack);
  } else if (cell.table) {
    let height = 0;
    for (const currentTableBodies of cell.table.body) {
      const innerTableHeights = currentTableBodies.map((innerTableCell) => {
        const size = findInlineSize(innerTableCell, maxWidth, usedWidth);
        usedWidth = size.width;
        innerTableCell._width = size.width;
        innerTableCell._height = size.height;
        return size.height;
      });
      height += Math.max(...innerTableHeights);
    }
    return {
      height,
      width: usedWidth,
    };
  } else if (cell._height) {
    usedWidth += cell._width;
    return {
      height: cell._height,
      width: usedWidth,
    };
  }

  return {
    height: null,
    width: usedWidth,
  };
}

export const measureTableRow = function (node, rowIndex) {
  const cellSizes = node.table.body[rowIndex].map((innerNode, i) => {
    const width = node.table.widths[i]._calcWidth;
    const height = findInlineSize(innerNode, width).height;
    innerNode._width = width;
    innerNode._height = height;
    return { width, height };
  });
  return cellSizes.reduce(
    (prev, next) => {
      return {
        height: Math.max(prev.height, next.height),
        width: prev.width + next.width,
      };
    },
    { height: 0, width: 0 }
  );
};
